<template>
  <div class="field" @mouseleave="hideList">
    <div class="control has-icons-left has-icons-right">
      <input class="input" type="text" autocomplete="off" @input="handleChange" :value="phoneNumber" :placeholder="intlData.example" @keyup.enter="handleCallback" :disabled="!ready" />
      <div class="flag-container" @click.stop="toggeList">
        <div class="selected-flag">
          <div :class="`iti-flag ${countryCode}`"></div>
          <div :class="`iti-arrow${isVisiblePanel ? ' up' : ''}`"></div>
        </div>
      </div>
      <span class="icon is-small is-right">
        <i class="fal" :class="{'fa-check has-text-success': valid, 'fa-times has-text-danger': !valid}"></i>
      </span>
      <ul class="country-list" v-if="isVisiblePanel">
        <li v-for="item in itemsData.frontItems" :key="item.code" class="country" :class="{active: item.code === countryCode}" @click.stop="setCountryCode(item.code)">
          <div class="flag-box">
            <div :class="`iti-flag ${item.code}`"></div>
          </div>
          <span class="country-name">{{ item.name }}</span>
          <span class="dial-code">+{{ item.dialCode }}</span>
        </li>
        <li class="divider" v-if="itemsData.needSeparator"></li>
        <li v-for="item in itemsData.otherItems" :key="item.code" class="country" :class="{active: item.code === countryCode}" @click.stop="setCountryCode(item.code)">
          <div class="flag-box">
            <div :class="`iti-flag ${item.code}`"></div>
          </div>
          <span class="country-name">{{ item.name }}</span>
          <span class="dial-code">+{{ item.dialCode }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import phonesData from './data'

import location from '@/libs/location'

const top = ['au', 'br', 'ca', 'cn', 'de', 'in', 'tr', 'gb', 'us']

export default {
  props: ['value', 'callback', 'country'],

  data: function () {
    return {
      ready: false,
      availableData: phonesData,
      phoneNumber: '',
      countryCode: top[0],
      isVisiblePanel: false,
      formatter: null,
      util: null,
      valid: false,
      emitCount: 0,
      phoneFormat: null
    }
  },

  computed: {
    phoneLib: function () {
      return import(/* webpackChunkName: "google-libphonenumber" */ 'google-libphonenumber')
    },

    intlData: function () {
      return this.availableData[this.countryCode]
    },

    itemsData: function () {
      const frontItems = []
      const otherItems = []
      const phonesDataCopy = Object.assign({}, this.availableData)

      top.forEach((code) => {
        const item = phonesDataCopy[code]

        if (item) {
          frontItems.push(item)
          delete phonesDataCopy[code]
        }
      })

      Object.values(phonesDataCopy).forEach(item => otherItems.push(item))
      const needSeparator = frontItems.length > 0 && otherItems.length > 0

      return {
        frontItems,
        otherItems,
        needSeparator
      }
    }
  },

  methods: {
    handleChange: function (event) {
      const value = event.target.value
      this.formatter.clear()

      if (value.length === 0) {
        this.phoneNumber = ''
      } else {
        for (const char of value) {
          if (char.match(/[0-9]/)) {
            this.phoneNumber = this.formatter.inputDigit(char)
          }
        }
      }

      this.emit()
    },

    setCountryCode: function (code) {
      if (!this.availableData[code]) {
        return
      }

      this.countryCode = code
      this.hideList()
      this.resetUtils()

      this.emit()
    },

    showList: function () {
      this.isVisiblePanel = true
    },

    hideList: function () {
      this.isVisiblePanel = false
    },

    toggeList: function () {
      (this.isVisiblePanel ? this.hideList : this.showList)()
    },

    getFormattedNumber: function () {
      this.valid = false

      if (this.phoneNumber.length === 0) {
        return
      }

      const number = this.parseForCountry(this.phoneNumber, this.countryCode)
      if (number) {
        this.valid = true
        return this.util.format(number, this.phoneFormat.E164)
      }

      this.checkOtherCountries()

      return ''
    },

    parseForCountry: function (raw, country) {
      try {
        const number = this.util.parse(`+${this.availableData[country].dialCode}${raw}`, country)
        if (this.util.isValidNumber(number)) {
          return number
        }
        return false
      } catch (err) {
        return false
      }
    },

    emit: function () {
      const count = ++this.emitCount
      const number = this.getFormattedNumber(count)
      if (count === this.emitCount) {
        this.$emit('input', number)
      }
    },

    checkIP: function () {
      location.get().then((response) => {
        this.setCountryCode(response.data.country_code.toLowerCase())
      })
    },

    resetUtils: function () {
      this.ready = false
      import(/* webpackChunkName: "google-libphonenumber" */ 'google-libphonenumber').then((lib) => {
        this.formatter = new lib.AsYouTypeFormatter(this.countryCode)
        this.util = lib.PhoneNumberUtil.getInstance()
        this.phoneFormat = lib.PhoneNumberFormat
        this.ready = true
      })
    },

    checkOtherCountries: function () {
      let match = null
      const input = this.phoneNumber.replace(/[^0-9]/g, '')
      Object.keys(this.availableData).forEach((key) => {
        if (!match && key !== this.countryCode) {
          const code = `${this.availableData[key].dialCode}`
          if (input.length >= code.length) {
            if (input.substring(0, code.length) === code) {
              const number = this.parseForCountry(input.substring(code.length), key)
              if (number) {
                match = true
                this.phoneNumber = this.util.format(number, this.phoneFormat.NATIONAL)
                this.setCountryCode(key)
              }
            }
          }
        }
      })
    },

    handleCallback: function () {
      if (this.callback) {
        this.callback()
      }
    }
  },

  mounted () {
    this.setCountryCode(top[0])

    if (this.value) {
      this.phoneNumber = this.value
      this.getFormattedNumber()
    } else if (this.country) {
      this.setCountryCode(this.country.toLowerCase())
    } else {
      this.checkIP()
    }
  }
}
</script>

<style lang="scss" scoped>
  @import 'styles.scss';
</style>
